import dayjs from 'dayjs';

/** Skip secrets and internal hashes in the ops-facing view. */
const HIDDEN_FIELDS = new Set([
  'password', 'org_password', 'facility_password', 'facility_users_password',
  'password_hash', 'hash', 'jwt', 'token', 'remember_token', 'api_key', 'secret',
  'otp', 'pin',
]);

const FIELD_LABELS = {
  id: 'Record ID',
  name: 'Name',
  full_name: 'Full name',
  first_name: 'First name',
  firstName: 'First name',
  middle_name: 'Middle name',
  surname: 'Surname',
  Surname: 'Surname',
  last_name: 'Last name',
  title: 'Title',
  job_title: 'Job title',
  gender: 'Gender',
  religion: 'Religion',
  marital_status: 'Marital status',
  date_of_birth: 'Date of birth',
  relationship: 'Relationship',
  email: 'Email',
  phone: 'Phone',
  contact_address: 'Address',
  address: 'Address',
  city: 'City',
  state: 'State',
  lga: 'Local government',
  website: 'Website',

  policy_no: 'Policy number',
  insurance_no: 'Insurance number',
  insurance_package: 'Plan / package',
  premium_type: 'Premium type',
  policy_cost: 'Policy cost',
  package_size: 'Package size',
  enrolee_size: 'Enrollee count',
  additional_heads: 'Additional heads',
  expiry_date: 'Expiry date',
  paymentStatus: 'Payment status',
  payment_status: 'Payment status',
  payment_id: 'Payment ID',
  category: 'Category',
  social_no: 'Organization code',
  org_social_no: 'Organization code',
  org_name: 'Organization name',
  org_code: 'Organization code',
  organization_name: 'Organization name',
  association_id: 'Association',
  plans_id: 'Plan',
  plan_name: 'Plan name',
  plan_id: 'Plan',
  plan_type: 'Plan type',
  role: 'Role',

  status: 'Status',
  flagged: 'Flagged',
  flag_reason: 'Flag reason',
  update_required: 'Needs update',
  updated: 'Marked updated',
  disclaimer: 'Disclaimer accepted',
  existing_medical_condition: 'Existing medical condition',
  previous_medical_condition: 'Previous medical condition',
  primary_health_facility: 'Primary facility',
  secondary_health_facility: 'Secondary facility',
  financier_id: 'Financier',
  pic_name: 'Photo',
  id_name: 'ID document',

  facility_id: 'Facility',
  facility_name: 'Facility name',
  facility_type: 'Facility type',
  facility_description: 'Description',
  facility_email: 'Email',
  facility_phone: 'Phone',
  facility_designation: 'Designation',
  facility_website: 'Website',
  facility_address: 'Address',
  facility_city: 'City',
  facility_state: 'State',
  facility_role: 'Role',
  facility_status: 'Status',
  facility_plan_type: 'Plan type',
  facility_staff_id: 'Staff code',
  facility_date: 'Created',
  facility_users_id: 'User ID',
  facility_users_full_name: 'Name',
  facility_users_email: 'Email',
  facility_users_phone: 'Phone',
  facility_users_role: 'Role',
  facility_users_status: 'Status',
  user_reg_date: 'Registered',

  biodataId: 'Profile ID',
  departmentId: 'Department',
  roleId: 'Role',
  staffId: 'Staff code',
  staffRoleId: 'Staff role',

  checkin_id: 'Encounter',
  checkinType: 'Encounter type',
  checkin_type_id: 'Encounter type',
  checkin_type_name: 'Encounter type',
  checkin_status: 'Encounter status',
  checkin_date: 'Checked in',
  checkout_date: 'Checked out',
  medical_complaint: 'Medical complaint',
  code: 'Code',

  claims_id: 'Claim',
  claims_status: 'Claim status',
  claims_queried: 'Queried',
  claims_comment: 'Comment',
  claims_date: 'Claim date',
  date_forwarded_hm: 'Sent to medical review',
  date_forwarded_cfo: 'Sent to finance',
  date_paid: 'Paid on',
  payment_batch: 'Payment batch',
  medical_complaint_hm: 'Medical review note',
  claim_item_status: 'Item status',
  claim_flag: 'Flagged',
  claim_comment: 'Comment',
  approved_cost: 'Approved cost',
  cost: 'Cost',
  quantity: 'Quantity',
  dosage: 'Dosage',
  duration: 'Duration',
  frequency: 'Frequency',
  auth_code: 'Authorization code',
  item_id: 'Item',
  item_name: 'Item name',
  item_code: 'Item code',
  item_brand: 'Brand',
  category_id: 'Category',
  comment: 'Comment',

  date: 'Date',
  created_at: 'Created',
  updated_at: 'Last updated',
  updated_date: 'Last updated',
  deleted: 'Deleted',
  is_active: 'Active',
  is_system_role: 'System role',
  display_name: 'Display name',
  description: 'Description',
  user_type: 'User type',
};

/**
 * Raw `logs_target_table` names are meaningless to ops ("costable_pricing"
 * says nothing about which price on which facility changed). The API sends a
 * `target_type_label` for every row now — this map is the client-side fallback
 * and must stay in sync with AdminLogsTargetResolver::TABLE_LABELS in api_v1.
 */
const TABLE_LABELS = {
  organizations: 'Organization',
  corporations: 'Organization',
  orgganization_plan: 'Organization plan',
  clients: 'Policy',
  client_biodata: 'Enrollee',
  staff: 'Admin staff',
  staff_biodata: 'Admin staff profile',
  user_data: 'User account',
  sessions: 'Session',
  facility: 'Facility',
  facilities: 'Facility',
  facility_users: 'Facility user',
  facility_suggestion: 'Facility suggestion',
  facility_types: 'Facility type',
  facility_tariff_items: 'Facility tariff item',
  tariff_items: 'Tariff item',
  checkin: 'Encounter / PA',
  claims: 'Claim',
  claim_items: 'Claim / PA item',
  queries: 'Encounter query',
  flags: 'Flag',
  appointments: 'Appointment',
  plans: 'Plan',
  plans_limits: 'Plan limit (legacy)',
  plan_limit_definitions: 'Plan limit',
  plans_categories: 'Plan category',
  plans_categories_items: 'Plan category item',
  plans_plans_category: 'Plan / category link',
  plans_items: 'Plan item',
  plans_summary: 'Plan summary',
  costable_pricing: 'Facility price',
  costable_items: 'Costable item',
  costable_category: 'Costable category',
  standard_item_types: 'Standard item type',
  checkin_types: 'Encounter type',
  care_programs: 'Care program',
  organization_program_settings: 'Organization care program',
  limit_metric_types: 'Limit metric type',
  verifications: 'Verification code',
  tiers: 'Tier',
  payments: 'Payment',
  policy_payments: 'Policy payment',
  notifications: 'Notification',
  sms_provider_settings: 'SMS provider settings',
  ops_email_recipient_settings: 'Ops email recipients',
  identity_generation_settings: 'Identity generation settings',
  rbac_roles: 'Role',
  rbac_permissions: 'Permission',
  rbac_user_roles: 'User role',
  deals: 'Deal',
};

const ACTION_LABELS = {
  create: 'Created',
  register: 'Registered',
  update: 'Updated',
  delete: 'Deleted',
  approve: 'Approved',
  approve_co: 'Claims officer approved',
  approve_hm: 'Medical review approved',
  reject: 'Rejected',
  submit: 'Submitted',
  submit_preauth: 'Submitted pre-authorization',
  submit_claim: 'Submitted claim',
  pay: 'Paid',
  pay_claim: 'Paid claim',
  query: 'Queried',
  flag: 'Flagged',
  unflag: 'Unflagged',
  propose: 'Proposed',
  login: 'Signed in',
  logout: 'Signed out',
  upload: 'Uploaded',
  upload_claim_items: 'Uploaded claim items',
  upload_manual_claim: 'Uploaded manual claim',
  cehckin: 'Checked in',
  checkin: 'Checked in',
  cehckout: 'Checked out',
  checkout: 'Checked out',
  password_reset: 'Reset password',
  payment_initiated: 'Payment started',
  payment_successful: 'Payment succeeded',
  payment_failed: 'Payment failed',
  payment_refunded: 'Payment refunded',
  payment_verified: 'Payment verified',
  payment_verification_failed: 'Payment verification failed',
  payment_verification_successful: 'Payment verification succeeded',
  message_send: 'Sent message',
  message_update: 'Updated message',
  read_message_query: 'Read query',
  log: 'Logged',
};

const DATE_FIELD_HINT = /(^|_)(date|at|on)$|(_date|_at|_on$)|expiry|created|updated|forwarded|paid|checkin|checkout|reg_date/i;
const FLAG_FIELD_HINT = /^(status|flagged|deleted|active|enabled|disclaimer|updated|update_required|claims_queried|is_active|is_system_role)$|(^|_)(status|flag|deleted|active|enabled|required)$/i;

function splitKey(key) {
  return String(key || '')
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/[_\-.]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

export function humanizeFieldName(key) {
  if (!key) return '—';
  if (FIELD_LABELS[key]) return FIELD_LABELS[key];
  const lower = String(key);
  if (FIELD_LABELS[lower]) return FIELD_LABELS[lower];
  let label = splitKey(key)
    .replace(/\bno\b/gi, 'number')
    .replace(/\bid\b/gi, 'ID')
    .replace(/\buri\b/gi, 'file')
    .replace(/\bimg\b/gi, 'image');
  label = label.replace(/\b\w/g, (c) => c.toUpperCase()).replace(/\bId\b/g, 'ID');
  return label;
}

export function humanizeTableName(table) {
  if (!table) return 'Record';
  const key = String(table);
  if (TABLE_LABELS[key]) return TABLE_LABELS[key];
  return splitKey(key).replace(/\b\w/g, (c) => c.toUpperCase()).replace(/s$/, '') || 'Record';
}

export function humanizeActionName(name) {
  if (!name) return '—';
  const key = String(name).toLowerCase();
  if (ACTION_LABELS[key]) return ACTION_LABELS[key];
  return splitKey(name).replace(/\b\w/g, (c) => c.toUpperCase());
}

export function isHiddenLogField(key) {
  const k = String(key || '').toLowerCase();
  if (HIDDEN_FIELDS.has(k) || HIDDEN_FIELDS.has(key)) return true;
  return /(password|secret|token|hash|otp|pin)$/i.test(k);
}

export function formatLogDate(value) {
  if (!value) return '—';
  const d = dayjs(value);
  return d.isValid() ? d.format('DD MMM YYYY, HH:mm:ss') : String(value);
}

function looksLikeDate(key, value) {
  if (typeof value !== 'string' && typeof value !== 'number') return false;
  if (!DATE_FIELD_HINT.test(String(key))) {
    return typeof value === 'string' && /^\d{4}-\d{2}-\d{2}/.test(value) && dayjs(value).isValid();
  }
  return dayjs(value).isValid();
}

function formatFlagValue(key, value) {
  const on = value === 1 || value === '1' || value === true || value === 'true';
  const off = value === 0 || value === '0' || value === false || value === 'false';
  if (!on && !off) return null;
  const k = String(key).toLowerCase();
  if (k.includes('flag')) return on ? 'Flagged' : 'Not flagged';
  if (k.includes('payment')) return on ? 'Paid' : 'Unpaid';
  if (k.includes('queried')) return on ? 'Queried' : 'Not queried';
  if (k.includes('required')) return on ? 'Yes' : 'No';
  if (k.includes('deleted')) return on ? 'Deleted' : 'Not deleted';
  if (k.includes('disclaimer')) return on ? 'Accepted' : 'Not accepted';
  if (k === 'updated') return on ? 'Yes' : 'No';
  if (k.includes('status') || k.includes('active') || k.includes('enabled')) {
    return on ? 'Active' : 'Inactive';
  }
  if (FLAG_FIELD_HINT.test(String(key))) return on ? 'Yes' : 'No';
  return null;
}

export function formatFieldValue(key, value) {
  if (isHiddenLogField(key)) return 'Hidden';
  if (value === null || value === undefined || value === '') return '—';
  if (typeof value === 'object') {
    try {
      return JSON.stringify(value, null, 2);
    } catch (e) {
      return String(value);
    }
  }
  const flagged = formatFlagValue(key, value);
  if (flagged) return flagged;
  if (looksLikeDate(key, value)) return formatLogDate(value);
  return String(value);
}

export function visibleFieldKeys(before = {}, after = {}) {
  return Array.from(new Set([...Object.keys(before || {}), ...Object.keys(after || {})]))
    .filter((key) => !isHiddenLogField(key))
    .sort((a, b) => humanizeFieldName(a).localeCompare(humanizeFieldName(b)));
}

export function recordTypeLabel(row) {
  // The API resolves this against the live record; fall back to the local map
  // for rows served by an older API build.
  return row?.target_type_label || humanizeTableName(row?.logs_target_table);
}

export function recordIdentityLabel(row) {
  const name = row?.target_display;
  const id = row?.logs_target_id;
  if (name && id && String(name) !== String(id)) return `${name} (${id})`;
  return name || id || '—';
}

/**
 * Where a target should take you when clicked. Only routes that actually
 * accept an id are linked; everything else is handled as a "search the log
 * for this record" action by the caller.
 */
export function targetRoute(link) {
  if (!link || !link.type) return null;
  if (link.type === 'facility' && link.id) return `/facilities/${encodeURIComponent(link.id)}`;
  if (link.type === 'organization') {
    // /corporations/users/:org expects the base64 hash of org_social_no.
    const social = link.social_no;
    if (social && typeof btoa === 'function') {
      try {
        return `/corporations/users/${encodeURIComponent(btoa(String(social)))}`;
      } catch (e) {
        return null;
      }
    }
    return null;
  }
  if (link.type === 'plan') return '/plans';
  return null;
}

/** Identifier a target can be re-searched by inside the activity log itself. */
export function targetSearchTerm(row) {
  const link = row?.target_link;
  if (link && ['enrollee', 'policy', 'encounter', 'claim'].includes(link.type) && link.id) {
    return String(link.id);
  }
  const id = row?.logs_target_id;
  return id ? String(id) : null;
}
